import type { LucideIcon } from 'lucide-react-native';
import { twJoin } from 'tailwind-merge';

import type { HeaderItemRole, HeaderItemTone } from '@/components/headerItems/types';
import { Button, ButtonIcon, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';

const ROLE_TONE: Record<HeaderItemRole, string> = {
  default: 'font-normal text-typography-900',
  confirm: 'font-semibold text-primary-600',
  destructive: 'font-normal text-error-600',
  accent: 'font-medium text-primary-700',
};

const ON_BRAND_TONE = 'font-normal text-typography-white';

type HeaderTextButtonProps = {
  label: string;
  onPress: () => void;
  icon?: LucideIcon;
  role?: HeaderItemRole;
  tone?: HeaderItemTone;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
};

/**
 * A header action that is a word: Save, Cancel, Close, Invite. Every one of them is this
 * component, so they share one height, one size of type and one horizontal padding, and a
 * word in one header never sits tighter in its slot than a word in another.
 *
 * The padding is the same on both sides of the label whether or not an icon leads it. That
 * matters most on iOS 26, where a custom header view such as Invite is wrapped in a glass
 * capsule sized to its content: the padding here is what gives that capsule the same ends as
 * the capsule the system draws around a native text button like Save.
 *
 * The neutral tone matches the bar's own back arrow. Only the confirming action and an accent
 * such as Invite take the brand color. On a bar painted in the brand color every word is
 * light instead, whatever its role, since the role colors are picked for a neutral bar.
 */
export function HeaderTextButton({
  label,
  onPress,
  icon,
  role = 'default',
  tone = 'default',
  accessibilityLabel,
  accessibilityHint,
  isDisabled = false,
  isLoading = false,
}: HeaderTextButtonProps) {
  const { colors } = useColors();

  const isInactive = isDisabled || isLoading;

  const toneClass = tone === 'onBrand' ? ON_BRAND_TONE : ROLE_TONE[role];

  return (
    <Button
      variant="link"
      className={twJoin('h-12 gap-1.5 px-3', isInactive && 'opacity-50')}
      onPress={onPress}
      disabled={isInactive}
      {...getAccessibilityProps({
        label: accessibilityLabel ?? label,
        role: 'button',
        hint: accessibilityHint,
      })}>
      {isLoading ? <ButtonSpinner color={colors.primary[200]} /> : null}

      {!isLoading && icon ? (
        <ButtonIcon as={icon} className={twJoin('h-6 w-6', toneClass)} />
      ) : null}

      {!isLoading ? <ButtonText className={toneClass}>{label}</ButtonText> : null}
    </Button>
  );
}
